import { ThemeProvider } from '@workboard/wobo-ui';
import { Relationships, useRelationshipsCount } from '@workboard/action-item';
import { QueryProvider } from '@workboard/project-management';
import classNames from 'classnames';
import React, { FC, useCallback, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';
import styled from 'styled-components';

import ErrorBoundary from '../../components/error-boundary';
import './MyWork/index';
import { ToastNotificationProvider } from './notifications';
import { getFeatureFlag } from './utils/getFeatureFlag';
import './ActionItemPopup';
import './RemindMeModal';
import './RepeatAiModal';
import './MirrorAiModal';
import './CopyAiModal';
import { FormData } from './CopyAiModal/useCopyAIForm';
import { GlobalConfigProvider } from './global/ConfigProvider';

declare global {
  interface Window {
    aiCopyAndLink: (id: string, initialData?: Partial<FormData>) => void;
    getAiLeftNoteCache: () => string;
    initActionItemDialogComp?: () => void;
    aiId?: string;
  }
}

const TabHandle = styled.button`
  border: solid 0px transparent;
  background: transparent;
  color: #333333;
  vertical-align: baseline;
  cursor: pointer;
  text-align: left;
`;

const ActionItemDialogContent: FC = () => {
  const [visibleTab, setVisibleTab] = useState<null | 'relationship'>(null);

  const activateTab = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (visibleTab === 'relationship') {
        return;
      }
      const containerEl = $('.left-data-container');
      containerEl.html('<div id="ai-additional-tab-content"></div>');
      $('.ai-tab-handles .tab-active').removeClass('tab-active');
      $('.ai-tab-handles .active').removeClass('active');
      $('#current-left-container-element').val('5');
      setVisibleTab('relationship');
    },
    [visibleTab]
  );

  useLayoutEffect(() => {
    const handler = () => {
      if (visibleTab !== null) {
        // leaving relationships tab
        setVisibleTab(null);
      }
    };
    $('.ai-tab-handles').on('click', '.ai-tab-handle', handler);
    return () => {
      $('.ai-tab-handles').off('click', '.ai-tab-handle', handler);
    };
  }, [visibleTab]);
  const urlSearchParams = new URLSearchParams(window.location.search);
  const aiId = window.aiId ?? urlSearchParams.get('id');
  const itemId = parseInt(aiId);
  const [relationshipsCount, setRelationshipsCount] =
    useRelationshipsCount(aiId);

  return (
    <>
      <TabHandle
        type="button"
        className={classNames(
          'ai-relationship',
          'ai-tabs-left',
          visibleTab === 'relationship' ? 'tab-active' : null
        )}
        onClick={activateTab}
      >
        <span
          className={classNames('ai-relationships-icon', 'ai-icons-left', {
            active: visibleTab === 'relationship',
          })}
          data-icon-id="5"
        >
          &nbsp;
        </span>
        {typeof relationshipsCount === 'number' ? (
          <span className="subactCount">{relationshipsCount}&nbsp;</span>
        ) : null}
        Related
      </TabHandle>

      {visibleTab === 'relationship'
        ? createPortal(
            <Relationships
              itemId={itemId}
              maxHeight={560}
              updateRelationshipCount={setRelationshipsCount}
            />,
            document.getElementById('ai-additional-tab-content')
          )
        : null}
    </>
  );
};

export const initActionItemDialog = () => {
  const relationshipsEnabled = getFeatureFlag('pmAIRelationshipsEnabled');
  const aiReactPopupEnabled = getFeatureFlag('pmAIReactPopupEnabled');

  const mountId = document.getElementById('ai-additional-tab-handles');
  if (!aiReactPopupEnabled && relationshipsEnabled && mountId) {
    createRoot(mountId).render(
      <ErrorBoundary>
        <ThemeProvider>
          <ToastNotificationProvider>
            <GlobalConfigProvider>
              <QueryProvider>
                <ActionItemDialogContent />
              </QueryProvider>
            </GlobalConfigProvider>
          </ToastNotificationProvider>
        </ThemeProvider>
      </ErrorBoundary>
    );
  }
};

window.initActionItemDialogComp = initActionItemDialog;
